/**
 * useIndustryCategoryFilter — Shared hook for industry → category dependent filtering.
 *
 * Given a full list of industries and categories (with industry_id),
 * computes which categories are valid for the selected industries
 * and prunes invalid category selections automatically.
 *
 * Used by both Discover and Home feeds.
 *
 * @version 1.0.0
 */
import { useMemo, useCallback, useEffect, useRef } from 'react';

export interface IndustryOption {
  id: string;
  label: string;
}

export interface CategoryOption {
  id: string;
  label: string;
  industry_id: string;
}

export interface UseIndustryCategoryFilterOptions {
  /** All available industries */
  industries: IndustryOption[];
  /** All available categories (each with industry_id) */
  categories: CategoryOption[];
  /** Currently selected industry IDs */
  selectedIndustryIds: string[];
  /** Currently selected category IDs */
  selectedCategoryIds: string[];
  /** Callback when invalid categories are pruned */
  onCategoryIdsPruned?: (validIds: string[]) => void;
}

export interface UseIndustryCategoryFilterReturn {
  /** Industries list (pass-through, stable reference) */
  industries: IndustryOption[];
  /** Categories filtered to selected industries (or all if none selected) */
  filteredCategories: CategoryOption[];
  /** Set of valid category IDs in current scope */
  validCategoryIds: Set<string>;
  /** Whether a given category ID is valid in current scope */
  isCategoryValid: (categoryId: string) => boolean;
}

export function useIndustryCategoryFilter({
  industries,
  categories,
  selectedIndustryIds,
  selectedCategoryIds,
  onCategoryIdsPruned,
}: UseIndustryCategoryFilterOptions): UseIndustryCategoryFilterReturn {
  // Filter categories based on selected industries
  const filteredCategories = useMemo(() => {
    if (selectedIndustryIds.length === 0) return categories;
    const industrySet = new Set(selectedIndustryIds);
    return categories.filter((c) => industrySet.has(c.industry_id));
  }, [categories, selectedIndustryIds]);

  // Valid category IDs set
  const validCategoryIds = useMemo(
    () => new Set(filteredCategories.map((c) => c.id)),
    [filteredCategories],
  );

  const isCategoryValid = useCallback(
    (categoryId: string) => validCategoryIds.has(categoryId),
    [validCategoryIds],
  );

  // Prune invalid categories when industries change
  const prevIndustryKey = useRef(selectedIndustryIds.join(','));

  useEffect(() => {
    const key = selectedIndustryIds.join(',');
    if (key === prevIndustryKey.current) return;
    prevIndustryKey.current = key;

    if (selectedCategoryIds.length === 0) return;

    const pruned = selectedCategoryIds.filter((id) => validCategoryIds.has(id));
    if (pruned.length !== selectedCategoryIds.length) {
      onCategoryIdsPruned?.(pruned);
    }
  }, [selectedIndustryIds, selectedCategoryIds, validCategoryIds, onCategoryIdsPruned]);

  return {
    industries,
    filteredCategories,
    validCategoryIds,
    isCategoryValid,
  };
}
